import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { IconButton } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import clsx from 'clsx'
import { onEscKeyPressOnDialog } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    dialogContainer: {
        '& .MuiDialog-paperScrollPaper': {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            maxWidth: pxToRem(785),
        },
    },
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            padding: theme.spacing(3.125, 0, 2.5),
        },
        maxWidth: pxToRem(998),
    },
    titleText: {
        color: '#304959',
        fontSize: pxToRem(34),
        maxWidth: pxToRem(998),
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
    dialogContent: {
        padding: theme.spacing(2.5, 0, 2.5, 0),
        overflow: 'hidden',
        color: theme.palette.common.black,
        fontSize: pxToRem(16),
        borderBottom: '1px solid #E0E0E0',
        minHeight: pxToRem(85),
    },
    dialogContentSmall: {
        width: pxToRem(500),
    },
    dialogContentDefault: {
        width: pxToRem(500),
        height: 'auto',
    },
    dialogContentLarge: {
        paddingLeft: 0,
        minWidth: 709,
        borderBottom: '1px solid #ACB1AD',
    },
    dialogActions: {
        '&.MuiDialogActions-root': {
            padding: theme.spacing(3.75, 0, 2.5),
        },
        '&.MuiDialogActions-spacing': {
            '& > *:not(first-child)': {
                marginLeft: theme.spacing(1.25),
            },
        },
    },
    actionButton: {
        color: '#1899D4',
        fontSize: pxToRem(16),
        fontWeight: 500,
        marginBottom: 16,
        border: '1px solid #1899D4',
        borderRadius: pxToRem(50),
        background: 'transparent',
        boxShadow: 'none',
        '&:hover': {
            background: 'transparent',
            boxShadow: 'none',
        },
    },
    confirmationText: {
        paddingTop: theme.spacing(2),
        fontSize: pxToRem(16),
        color: theme.palette.common.black,
    },
    successIcon: {
        color: '#30b27e',
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
}))

export interface WorkspaceConfirmationDialogProps {
    /**
     * Controls the open state of the dialog
     */
    openDialog: boolean

    /**
     * Callback for closing the dialog
     */
    handleDialogClose: VoidFunction

    /**
     * Callback for the confirmation action
     */
    dialogActionCallback?: (_: React.MouseEvent<Element, MouseEvent>) => void

    /**
     * Dialog heading text
     */
    dialogHeading?: string

    /**
     * Custom content to display in the dialog
     */
    contentNode?: React.ReactNode

    /**
     * Confirmation message displayed above action buttons
     */
    confirmMessage?: string

    /**
     * Text for the cancel button
     */
    cancelText?: string

    /**
     * Text for the confirm button
     */
    confirmText?: string

    /**
     * Disables the confirm and cancel buttons
     */
    isConfirmButtonDisabled?: boolean

    /**
     * Size of the dialog
     */
    size?: 'small' | 'default' | 'large'

    /**
     * Type of dialog (determines icon shown)
     */
    dialogType?: 'warning' | 'error' | 'success'

    /**
     * Custom action buttons to replace default buttons
     */
    customDialogActions?: React.ReactNode

    /**
     * ID of the element that describes the dialog content for accessibility
     */
    ariaDescribedBy?: string
}

export const WorkspaceConfirmationDialog: React.FC<WorkspaceConfirmationDialogProps> = ({
    openDialog,
    handleDialogClose,
    dialogActionCallback,
    dialogHeading = 'Confirm',
    contentNode,
    confirmMessage = 'Are you sure you want to proceed?',
    cancelText = 'Cancel',
    confirmText = 'Confirm',
    isConfirmButtonDisabled = false,
    size = 'small',
    dialogType,
    customDialogActions,
    ariaDescribedBy,
}) => {
    const classes = useStyles()

    return (
        <Dialog
            open={openDialog}
            data-testid="workspace-confirmation-dialog"
            className={classes.dialogContainer}
            aria-labelledby="workspace-confirmation-dialog-title"
            aria-describedby={ariaDescribedBy}
            maxWidth="lg"
            onClose={onEscKeyPressOnDialog(handleDialogClose)}
        >
            <DialogTitle className={classes.dialogTitle}>
                <div className={classes.titleContainer}>
                    {dialogType === 'warning' && (
                        <IconButton>
                            <ReportProblemOutlinedIcon
                                fontSize="small"
                                style={{ color: '#F0451F' }}
                            />
                        </IconButton>
                    )}

                    {dialogType === 'success' && (
                        <IconButton>
                            <CheckCircleOutline
                                data-testid="success-icon"
                                className={classes.successIcon}
                            />
                        </IconButton>
                    )}

                    <Typography
                        className={classes.titleText}
                        aria-label={dialogHeading}
                    >
                        {dialogHeading}
                    </Typography>
                </div>
            </DialogTitle>

            <DialogContent
                className={clsx(
                    classes.dialogContent,
                    size === 'small' && classes.dialogContentSmall,
                    size === 'default' && classes.dialogContentDefault,
                    size === 'large' && classes.dialogContentLarge
                )}
                data-testid="workspace-dialog-content"
            >
                {contentNode}
                {confirmMessage?.length !== 0 && confirmMessage !== ' ' && (
                    <Typography className={classes.confirmationText}>
                        {confirmMessage}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                {!customDialogActions && (
                    <>
                        {!!cancelText && (
                            <Button
                                role="button"
                                type="button"
                                data-testid="workspace-dialog-cancel"
                                className={classes.actionButton}
                                onClick={handleDialogClose}
                                variant="contained"
                                aria-label={cancelText}
                                disabled={isConfirmButtonDisabled}
                            >
                                {cancelText}
                            </Button>
                        )}

                        {!!confirmText && (
                            <Button
                                data-testid="workspace-dialog-confirm"
                                onClick={dialogActionCallback}
                                variant="contained"
                                className={classes.actionButton}
                                disabled={isConfirmButtonDisabled}
                            >
                                {confirmText}
                            </Button>
                        )}
                    </>
                )}
                {customDialogActions}
            </DialogActions>
        </Dialog>
    )
}
